import React, { useEffect, useRef, useState } from 'react';
import * as am5xy from '@amcharts/amcharts5/xy';
import './AnalysisTab.css';
import searchIcon from '../../Images/search-icon.svg';
import filterIcon from '../../Images/settings-icon.svg'; // Using settings icon as filter
import downIcon from '../../Images/chevron-down.svg';
import calendarIcon from '../../Images/calendar.svg';
import downloadIcon from '../../Images/download-icon.svg';
import { getAssetTypes, AssetTypeResponse, queryTelemetryData, TelemetryQueryRequest, downloadTelemetryData, TelemetryDownloadRequest, getAssetDetails, AssetDetailsResponse, AssetAttribute } from '../../Services/serviceURL';
import DateRangePicker from '../../Components/common/date-range-picker/DateRangePicker';
import { SidebarOption } from '../../Components/common/date-range-picker/types';
// Add this import at the top
import colorPickerIcon from '../../Images/color-picker.svg';
import scaleIcon from '../../Images/scale.png';
import Loader from '../../Components/common/loader/loader';
import IndividualChart from './IndividualChart';
import {
  canExportAnalysisData,
  formatAnalysisCalendarButtonLabel,
  matchPredefinedTimeRangeLabel,
  shouldShowGroupChartEmptyState,
} from './analysisTabDisplay';
import { getTimezoneShortName, parseLocalDateTime } from './analysisTabDateTime';
import { buildChartModeStatePatch } from './analysisTabChartMode';
import { buildSensorsFromAssetTypesResponse } from './analysisTabAssetTypeSensors';
import type { SensorData } from './analysisTabSensorTypes';
import {
  applyTelemetryToSensors,
  buildTelemetryQueryRequest,
  filterUniqueKnownChannels,
  isValidAssetGuid,
  sensorsHaveAnyData,
} from './analysisTabTelemetryUtils';
import {
  buildUnitScalingInitialValues,
  getModifiedValidScalingUnits,
  isCustomScalingApplyEnabled,
} from './analysisTabCustomScaling';
import { buildChartDataKey, getSelectedSensorData } from './analysisTabChartData';
import { getAnalysisDateRange, getRelativeTimeValue } from './analysisTabDateRange';
import { createSensorsFromAssetDetails } from './analysisTabSensorFactory';
import { useAnalysisGroupChart } from './useAnalysisGroupChart';

// Extended API Response interfaces to match the expected API structure
interface ExtendedAssetTypeParameter {
  tag: string;
  sensorName: string;
  isUserConfigurable?: boolean;
  units?: {
    code: string;
    description: string;
  }[];
  channelId: string;
  position?: string;
}

interface AnalysisTabProps {
  assetId?: string;
  assetType?: string; // Add assetType prop
  assetParameters?: AssetAttribute[]; // Asset attributes from getAssetDetails API
  triggerInitialLoad?: boolean;
}

const AnalysisTabContent: React.FC<AnalysisTabProps> = ({ assetId: propAssetId, assetType, assetParameters,triggerInitialLoad }) => {
  const [selectedSensors, setSelectedSensors] = useState<string[]>([]);
  const [pendingSensors, setPendingSensors] = useState<string[]>([]); // Temporary selection before Apply
  const [showFilterPopup, setShowFilterPopup] = useState<boolean>(false);
  const [searchInputVal, setSearchInputVal] = useState<string>('');
  const [sensors, setSensors] = useState<SensorData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [chartDataLoading, setChartDataLoading] = useState<boolean>(false);
  const [hasNoData, setHasNoData] = useState<boolean>(false);
  const [initialDataLoadComplete, setInitialDataLoadComplete] = useState<boolean>(false);
  const [sensorsInitialized, setSensorsInitialized] = useState<boolean>(false);
  const [chartCreated, setChartCreated] = useState<boolean>(false);
  const [lastChartDataKey, setLastChartDataKey] = useState<string>('');
  const [isTabVisible, setIsTabVisible] = useState<boolean>(true);
  const [hasValidChartData, setHasValidChartData] = useState<boolean>(false); // Track if we have valid data for chart
  const [chartDataSnapshot, setChartDataSnapshot] = useState<SensorData[]>([]); // Snapshot of chart data for persistence

  // Calendar modal and datetime picker states
  const [showCalendarModal, setShowCalendarModal] = useState<boolean>(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('Last 1 hour');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [appliedStartDate, setAppliedStartDate] = useState<Date | null>(null);
  const [appliedEndDate, setAppliedEndDate] = useState<Date | null>(null);
  // Add these with your existing useState declarations
  const [showColorPickerFor, setShowColorPickerFor] = useState<string | null>(null);
  const [colorPickerPosition, setColorPickerPosition] = useState<{ x: number; y: number } | null>(null);
  const chartInstanceRef = useRef<am5xy.XYChart | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const calendarModalRef = useRef<HTMLDivElement>(null);
  const telemetryFetchingRef = useRef<boolean>(false); // Prevent multiple simultaneous calls
  const telemetryTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Debounce mechanism
  const chartCreationRef = useRef<boolean>(false); // Prevent multiple simultaneous chart creations
  const chartCreationTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Debounce chart creation

  // Add this with your existing useRef declarations
  const colorPickerRef = useRef<HTMLDivElement>(null);
  const [showDatePicker,setShowDatePicker] = useState(false);
  const [selectedSidebarOption, setSelectedSidebarOption] = useState<string | null>(null);
  const [browserTimezone, setBrowserTimezone] = useState<string>(''); // Browser detected timezone

  // Custom Scaling Modal States
  const [showCustomScalingModal, setShowCustomScalingModal] = useState<boolean>(false);
  const [unitScalingValues, setUnitScalingValues] = useState<{
    [key: string]: { min: string; max: string; displayName?: string; actualUnit?: string }
  }>({});
  
  // Persistent state for manually entered scaling values (retains values between modal opens)
  const [manuallyEnteredScalingValues, setManuallyEnteredScalingValues] = useState<{
    [unit: string]: { min: string; max: string }
  }>({});
  
  // Track which units were actually modified by user during current modal session
  const [modifiedUnits, setModifiedUnits] = useState<Set<string>>(new Set());
  
  // Custom Scaling - now supports multiple units
  const [customScaling, setCustomScaling] = useState<{
    [unit: string]: { min: number; max: number }
  } | null>(null);
  
  // Chart mode state - Individual or Group
  const [chartMode, setChartMode] = useState<'individual' | 'group'>('individual');
  // Store individual mode sensor selection to restore when switching back
  const [individualModeSensors, setIndividualModeSensors] = useState<string[]>([]);
  // Store group mode sensor selection to restore when switching back
  const [groupModeSensors, setGroupModeSensors] = useState<string[]>([]);
  
  // Store uomRanges from API response
  const [uomRanges, setUomRanges] = useState<{
    uom: string;
    minValue: number;
    maxValue: number;
  }[]>([]);

  // Export functionality state
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [assetName, setAssetName] = useState<string>(''); // Store asset name for file naming


  // Sidebar options for DateRangePicker (maps to our time range options)
  const sidebarOptions: SidebarOption[] = [
    'Last 1 hour',
    'Last 2 hours',
    'Last 4 hours',
    'Last 8 hours',
    'Last 24 hours',
    'Last 7 days'
  ];
    // Helper function to map selectedTimeRange to SidebarOption
  const getSelectedSidebarOption = (): SidebarOption | null => {
    // Only return a sidebar option if it's one of the predefined options, not custom
    if (selectedTimeRange === 'Custom date range') {
      return null;
    }
    // Check if the selectedTimeRange matches any sidebar option
    return sidebarOptions.find(option => option === selectedTimeRange) || null;
  };

  // Detect browser timezone on component mount
  useEffect(() => {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setBrowserTimezone(timezone);

  }, []);

  // Use prop assetId
  const assetId = propAssetId;


  // Initialize applied dates based on default time range on component mount
  useEffect(() => {
    if (!appliedStartDate && !appliedEndDate) {
      const now = new Date();
      const oneHourAgo = new Date(now.getTime() - 1 * 60 * 60 * 1000);
      setAppliedStartDate(oneHourAgo);
      setAppliedEndDate(now);
    }
  }, []);

  // Cleanup effect to clear timeouts and prevent memory leaks
  useEffect(() => {
    return () => {
      if (telemetryTimeoutRef.current) {
        clearTimeout(telemetryTimeoutRef.current);
      }
      if (chartCreationTimeoutRef.current) {
        clearTimeout(chartCreationTimeoutRef.current);
      }
      // Only dispose chart on unmount, not on state changes
      if (chartInstanceRef.current) {
        chartInstanceRef.current.root.dispose();
        chartInstanceRef.current = null;
      }
      // Reset flags
      telemetryFetchingRef.current = false;
      chartCreationRef.current = false;
    };
  }, []);

  // Color palette for sensors
  const colorPalette = [
    '#00FFFF',
    '#00789e',
    '#CD7F32',
    '#A52A2A',
    '#808000',
    '#AAFF00',
    '#097969',
    '#228B22',
    '#FBCEB1',
    '#FFAC1C',
    '#DE3163',
    '#F88379',
    '#FF00FF',
    '#800080',
    '#5D3FD3',
    '#F5DEB3',
    '#FF0000',
    '#FFEA00',
    '#000080'
  ];
  // Add this after the defaultColorPalette definition (around line 110)
  const colorOptions = [
    '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF',
    '#00FFFF', '#FFA500', '#800080', '#008000', '#000080',
    '#A52A2A', '#808000', '#FFC0CB', '#FFD700', '#E6E6FA',
    '#00FF7F', '#D2691E', '#DC143C', '#00CED1', '#8A2BE2'
  ];
  // API function to fetch sensors for a specific asset
  const fetchAssetSensors = async (assetId: string): Promise<SensorData[]> => {
    try {
      setLoading(true);

      const assetDetailsResponse = await getAssetDetails(assetId);

      if (assetDetailsResponse && assetDetailsResponse.attributes && assetDetailsResponse.attributes.length > 0) {
        // Store asset name for export filename
        if (assetDetailsResponse.assetName) {
          setAssetName(assetDetailsResponse.assetName);
        }

        // Filter attributes that have channelId to get only sensor parameters
        const sensorAttributes = assetDetailsResponse.attributes.filter(attr => attr.channelId);
        if (sensorAttributes.length > 0) {
          return createSensorsFromAssetDetails(sensorAttributes, assetType, colorPalette);
        } else {
          return await fetchAssetTypes();
        }
      } else {
        return await fetchAssetTypes();
      }
    } catch (error) {
      // Fallback to asset types if asset details fail
      return await fetchAssetTypes();
    } finally {
      setLoading(false);
    }
  };
  // Add these functions after your other handler functions

  const handleSensorColorChange = (sensorId: string, color: string, closePicker: boolean = true) => {
    setSensors(prevSensors =>
      prevSensors.map(sensor =>
        sensor.id === sensorId ? { ...sensor, color } : sensor
      )
    );

    // Reset chart so it recreates with new colors
    setChartCreated(false);
    setLastChartDataKey('');
    // Only close color picker if explicitly requested
    if (closePicker) {
      setShowColorPickerFor(null);
      setColorPickerPosition(null);
    }
  };

  const openColorPicker = (sensorId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    setColorPickerPosition({
      x: rect.left + window.scrollX,
      y: rect.bottom + window.scrollY + 5
    });

    setShowColorPickerFor(sensorId);
  };

  // Export functionality handler
  const handleExportData = async () => {
    // Prevent duplicate calls
    if (isExporting) {
      return;
    }
    
    
    if (!assetId || selectedSensors.length === 0) {
      alert('Please select sensors to export data');
      return;
    }

    setIsExporting(true);

    try {
      const Timerange = getRelativeTimeValue(selectedTimeRange);

      // Use the same payload structure as telemetryRequest in fetchTelemetryData
      const downloadRequest: TelemetryDownloadRequest = {
        AssetId: assetId,
        ChannelIds: selectedSensors,
        timeRange: Timerange,
      };

      // Apply the same conditional logic as fetchTelemetryData
      if (selectedTimeRange === 'Custom date range') {
        const { startTime, endTime } = getDateRange();
        downloadRequest.StartTime = startTime;
        downloadRequest.EndTime = endTime;
      } else {
        downloadRequest.timeRange = Timerange;
      }


      const response = await downloadTelemetryData(downloadRequest);
      
      // Create blob and download file
      const blob = new Blob([response.data], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with asset name and timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const fileNameBase = assetName || 'telemetry-data'; // Use asset name, fallback to assetId, then generic name
      // Clean filename to remove invalid characters
      const cleanFileName = fileNameBase.replace(/[^a-zA-Z0-9-_]/g, '-');
      link.download = `${cleanFileName}-${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      
    } catch (error) {
      console.error('Error exporting telemetry data:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // Custom Scaling Modal Handlers
  const handleCustomScalingOpen = () => {
    const selectedSensorsList = sensors.filter(sensor => selectedSensors.includes(sensor.channelId));
    const initialValues = buildUnitScalingInitialValues(
      selectedSensorsList,
      manuallyEnteredScalingValues,
      uomRanges
    );
    setUnitScalingValues(initialValues);
    setModifiedUnits(new Set());
    setShowCustomScalingModal(true);
  };

  const handleCustomScalingClose = () => {
    setShowCustomScalingModal(false);
    setUnitScalingValues({});
    setModifiedUnits(new Set()); // Reset modified units tracking
  };

  // Function to update min/max values for a specific unit
  const handleUnitValueChange = (unit: string, field: 'min' | 'max', value: string) => {
    // Mark this unit as modified
    setModifiedUnits(prev => new Set(prev).add(unit));
    
    // Update current modal values
    setUnitScalingValues(prev => ({
      ...prev,
      [unit]: {
        ...prev[unit],
        [field]: value
      }
    }));
    
    // Also update persistent manually entered values
    setManuallyEnteredScalingValues(prev => ({
      ...prev,
      [unit]: {
        ...prev[unit],
        min: field === 'min' ? value : prev[unit]?.min || '',
        max: field === 'max' ? value : prev[unit]?.max || ''
      }
    }));
  };

  const handleCustomScalingApply = () => {
    const modifiedValidUnits = getModifiedValidScalingUnits(unitScalingValues, modifiedUnits);
    
    if (modifiedValidUnits.length > 0) {
      // Dispose existing chart if it exists to force complete recreation
      if (chartInstanceRef.current) {
        chartInstanceRef.current.root.dispose();
        chartInstanceRef.current = null;
      }
      
      // Start with existing custom scaling or empty object
      const multiUnitScaling: { [unit: string]: { min: number; max: number } } = {
        ...(customScaling || {}) // Preserve existing custom scaling
      };
      
      // Only update scaling for modified units
      modifiedValidUnits.forEach(([key, values]) => {
        // Use the stored actualUnit from the values object
        const actualUnit = values.actualUnit || key;
        
        multiUnitScaling[actualUnit] = {
          min: parseFloat(values.min),
          max: parseFloat(values.max)
        };
      });
      
      // Set custom scaling (preserving unmodified units)
      setCustomScaling(multiUnitScaling);
      
      // Force chart recreation with new scaling
      setChartCreated(false);
      setLastChartDataKey(''); // Clear the data key to force recreation
      
      // Ensure chart recreates after a brief delay
      setTimeout(() => {
        setChartCreated(false); // Double-check chart recreation
      }, 100);
      

    }
    
    handleCustomScalingClose();
  };

  // Chart mode handlers
  const handleChartModeChange = (mode: 'individual' | 'group') => {
    setChartMode(mode);
    const patch = buildChartModeStatePatch(
      mode,
      chartMode,
      selectedSensors,
      individualModeSensors,
      groupModeSensors
    );
    setSelectedSensors(patch.selectedSensors);
    setPendingSensors(patch.pendingSensors);
    setIndividualModeSensors(patch.individualModeSensors);
    setGroupModeSensors(patch.groupModeSensors);
    if (patch.channelsToFetch.length > 0) {
      triggerTelemetryFetchWithChannels(patch.channelsToFetch);
    }
    setChartCreated(false);
    setLastChartDataKey('');
  };

  // API function to fetch asset types (keeping original for fallback)
  const fetchAssetTypes = async (): Promise<SensorData[]> => {
    try {
      setLoading(true);
      const assetTypesResponse = await getAssetTypes();
      return buildSensorsFromAssetTypesResponse(assetTypesResponse, colorPalette, assetType);
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Fetch sensors on component mount and when assetType or assetParameters change
  useEffect(() => {
    const loadSensors = async () => {
      // Reset data loading states when sensors are being reloaded
      setHasNoData(false);
      setInitialDataLoadComplete(false);
      setSensorsInitialized(false);

      let fetchedSensors: SensorData[];

      if (assetParameters && assetParameters.length > 0) {
        fetchedSensors = createSensorsFromAssetDetails(assetParameters, assetType, colorPalette);
      }
      else if (assetId) {
        fetchedSensors = await fetchAssetSensors(assetId);
      }
      else {
        fetchedSensors = await fetchAssetTypes();
      }

      setSensors(fetchedSensors);

      // Auto-select first 3 sensors with different channel IDs if any sensors were loaded
      if (fetchedSensors.length > 0) {
        // Get unique channel IDs to ensure variety in initial selection
        const channelIds = fetchedSensors.map(sensor => sensor.channelId);
        const uniqueChannelIds = channelIds.filter((id, index) => channelIds.indexOf(id) === index);
        const initialSelection = uniqueChannelIds.slice(0, 3);
        setSelectedSensors(initialSelection);
        setPendingSensors(initialSelection); // Initialize pending sensors
        setSensorsInitialized(true); // Mark as initialized
      } else {
        setHasNoData(true);
        setInitialDataLoadComplete(true);
        setSensorsInitialized(true); // Mark as initialized even with no sensors
      }
    };

    loadSensors();
  }, [assetId, assetType, assetParameters]); // Add assetId as dependency

  const getDateRange = () =>
    getAnalysisDateRange({
      selectedTimeRange,
      startDate,
      endDate,
      appliedStartDate,
      appliedEndDate,
    });

  // Calendar modal handlers
  const handleCalendarToggle = () => {
    if (!showCalendarModal) {
      // When opening the modal, calculate and set dates based on current time range
      if (appliedStartDate && appliedEndDate) {
        setStartDate(appliedStartDate);
        setEndDate(appliedEndDate);
      } else {
        // Calculate dates based on current selected time range
        const { startTime, endTime } = getDateRange();
        const calculatedStartDate = new Date(startTime);
        const calculatedEndDate = new Date(endTime);
        setStartDate(calculatedStartDate);
        setEndDate(calculatedEndDate);
      }
      // Reset sidebar option tracking when opening modal
      setSelectedSidebarOption(null);
    }
    setShowCalendarModal(!showCalendarModal);
  };

  const handleDateRangeApply = () => {
    // Reset chart flag since date range is changing
    setChartCreated(false);
    setHasValidChartData(false);
    setChartDataSnapshot([]); // Clear snapshot when changing date range
    setCustomScaling(null); // Reset custom scaling when date range changes
    setManuallyEnteredScalingValues({}); // Clear manually entered scaling values when date range changes
  };
  // Function to fetch telemetry data for selected sensors
  const fetchTelemetryData = async (channelIds: string[]): Promise<void> => {
    if (channelIds.length === 0) {
      return;
    }

    if (!assetId || assetId.trim() === '' || !isValidAssetGuid(assetId)) {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    if (telemetryFetchingRef.current) {
      return;
    }

    try {
      telemetryFetchingRef.current = true;
      setChartDataLoading(true);

      const telemetryRequest = buildTelemetryQueryRequest(
        assetId,
        channelIds,
        selectedTimeRange,
        getRelativeTimeValue(selectedTimeRange),
        getDateRange
      );
      const telemetryData = await queryTelemetryData(telemetryRequest);

      if (!telemetryData.Success || !telemetryData.Data?.telemetryData) {
        setHasNoData(true);
        setInitialDataLoadComplete(true);
        return;
      }

      if (telemetryData.Data.uomRanges && Array.isArray(telemetryData.Data.uomRanges)) {
        setUomRanges(telemetryData.Data.uomRanges);
      }

      const { startTime, endTime } = getDateRange();
      const startMs = parseLocalDateTime(startTime);
      const endMs = parseLocalDateTime(endTime);
      const updatedSensors = applyTelemetryToSensors(
        sensors,
        channelIds,
        telemetryData.Data.telemetryData,
        startMs,
        endMs
      );
      const hasAnyData = sensorsHaveAnyData(updatedSensors, channelIds);

      setSensors(updatedSensors);
      setHasNoData(!hasAnyData);
      setInitialDataLoadComplete(true);
      setHasValidChartData(hasAnyData);

      if (hasAnyData) {
        setChartDataSnapshot(
          updatedSensors.filter(sensor =>
            channelIds.includes(sensor.channelId) && sensor.dataPoints.length > 0
          )
        );
      }
    } catch (error) {
      console.error('Error fetching telemetry data:', error);
      setHasNoData(true);
      setInitialDataLoadComplete(true);
    } finally {
      telemetryFetchingRef.current = false;
      setChartDataLoading(false);
    }
  };
  const applyLastOneHour = () => {
  // UI state reset
  setSelectedTimeRange('Last 1 hour');

  const now = new Date();
  const oneHourAgo = new Date(now.getTime() - 1 * 60 * 60 * 1000);

  setStartDate(oneHourAgo);
  setEndDate(now);
  setAppliedStartDate(oneHourAgo);
  setAppliedEndDate(now);

  // Chart & scaling reset
  setChartCreated(false);
  setHasValidChartData(false);
  setChartDataSnapshot([]);
  setCustomScaling(null);
  setManuallyEnteredScalingValues({});

  // 🔥 ACTUAL API CALL
  triggerTelemetryFetch();
};

  const scheduleTelemetryFetch = (channelIds: string[]) => {
    if (telemetryTimeoutRef.current) {
      clearTimeout(telemetryTimeoutRef.current);
    }

    setHasNoData(false);
    setInitialDataLoadComplete(false);

    if (!assetId || assetId.trim() === '' || !isValidAssetGuid(assetId)) {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    if (sensors.length === 0 || channelIds.length === 0) {
      setHasNoData(true);
      setInitialDataLoadComplete(true);
      return;
    }

    telemetryTimeoutRef.current = setTimeout(() => {
      const uniqueChannelIds = filterUniqueKnownChannels(channelIds, sensors);
      if (uniqueChannelIds.length > 0) {
        fetchTelemetryData(uniqueChannelIds);
      }
    }, 300);
  };

  const triggerTelemetryFetch = () => scheduleTelemetryFetch(selectedSensors);

  const triggerTelemetryFetchWithChannels = (channelIds: string[]) => scheduleTelemetryFetch(channelIds);

  // Only fetch telemetry on initial load and datetime/asset changes (not sensor selection changes)
  useEffect(() => {
    // Only trigger after sensors are initialized to prevent multiple loads
    if (sensorsInitialized && sensors.length > 0 && selectedSensors.length > 0) {
      triggerTelemetryFetch();
    } else if (sensorsInitialized && selectedSensors.length === 0) {
      // If no sensors are selected, reset to show selection message
      setHasNoData(false);
      setInitialDataLoadComplete(true);
    }
  }, [assetId, selectedTimeRange, startDate, endDate, appliedStartDate, appliedEndDate, sensorsInitialized]); // Added appliedStartDate and appliedEndDate to dependencies

  // Effect to handle chart visibility when component becomes visible (e.g., tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsTabVisible(isVisible);

      if (isVisible && chartInstanceRef.current) {
        // Page became visible, resize chart to ensure proper display
        setTimeout(() => {
          if (chartInstanceRef.current && chartInstanceRef.current.root) {
            chartInstanceRef.current.root.resize();
          }
        }, 150);
      }
    };

    // Listen for visibility changes (tab switching)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Also check when the component mounts
    if (chartInstanceRef.current) {
      setTimeout(() => {
        if (chartInstanceRef.current && chartInstanceRef.current.root) {
          chartInstanceRef.current.root.resize();
        }
      }, 150);
    }

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [chartCreated]); // Depend on chartCreated to run when chart is available

  // Effect to handle when the analysis tab becomes visible within the application
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && chartInstanceRef.current) {
          // Chart container is visible, resize to ensure proper display without recreating
          setTimeout(() => {
            if (chartInstanceRef.current && chartInstanceRef.current.root) {
              chartInstanceRef.current.root.resize();
            }
          }, 100);
        }
      },
      { threshold: 0.1 }
    );

    const chartContainer = document.getElementById('analysis-chartdiv');
    if (chartContainer) {
      observer.observe(chartContainer);
    }

    return () => {
      observer.disconnect();
      if (chartContainer) observer.unobserve(chartContainer);
    };
  }, [chartCreated]);

  // Additional effect to handle tab switching and component visibility
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;

    const handleResize = () => {
      if (chartInstanceRef.current && chartInstanceRef.current.root) {
        // Clear any existing timeout to debounce resize calls
        if (resizeTimeout) {
          clearTimeout(resizeTimeout);
        }

        // Debounce resize to avoid rapid calls during tab switches
        resizeTimeout = setTimeout(() => {
          if (chartInstanceRef.current && chartInstanceRef.current.root) {
            chartInstanceRef.current.root.resize();
          }
        }, 150);
      }
    };

    // Listen for window resize events (can be triggered by tab switching)
    window.addEventListener('resize', handleResize);

    // Also trigger a resize when the chart is first created to ensure proper display
    if (chartCreated && chartInstanceRef.current) {
      handleResize();
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [chartCreated]);

  // Filter sensors based on search input
  const searchFiltered = sensors.filter(sensor =>
    sensor.sensorName.toLowerCase().includes(searchInputVal.toLowerCase())
  );

  const handleFilterDropdownClick = () => {
    if (!showFilterPopup) {
      // Initialize pending sensors with current selections when opening
      setPendingSensors(selectedSensors);
      setSearchInputVal(''); // Clear search field when opening dropdown
    }
    setShowFilterPopup(!showFilterPopup);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputVal(e.target.value);
  };

  const handleClearButton = () => {
    setPendingSensors([]);
    // Don't close the modal, let user continue selecting or click Apply
  };

  const handleApplyButton = () => {
    // Reset chart created flag since we're changing sensors
    setChartCreated(false);
    setHasValidChartData(false);
    setChartDataSnapshot([]); // Clear snapshot when changing sensors
    setCustomScaling(null); // Reset custom scaling when sensors change
    setManuallyEnteredScalingValues({}); // Clear manually entered scaling values when sensors change

    // Apply the pending sensor selections immediately
    setSelectedSensors(pendingSensors);
    
    // Save the sensor selection for the current chart mode
    if (chartMode === 'group') {
      setGroupModeSensors(pendingSensors);
    } else {
      setIndividualModeSensors(pendingSensors);
    }
    
    setShowFilterPopup(false);

    // Trigger API call with new selections directly (don't wait for state update)
    if (pendingSensors.length > 0) {
      triggerTelemetryFetchWithChannels(pendingSensors);
    } else {
      // If no sensors selected, show appropriate message
      setHasNoData(false);
      setInitialDataLoadComplete(true);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside filter dropdown
      const isClickInsideFilter = filterRef.current?.contains(target);
      // Check if click is inside calendar modal
      const isClickInsideCalendar = calendarModalRef.current?.contains(target);
      // Check if click is inside color picker
      const isClickInsideColorPicker = colorPickerRef.current?.contains(target);

      // Only close filter popup if click is outside AND not inside color picker
      if (showFilterPopup && !isClickInsideFilter && !isClickInsideColorPicker) {
        setShowFilterPopup(false);
      }

      // Only close calendar modal if click is outside
      if (showCalendarModal && !isClickInsideCalendar) {
        setShowCalendarModal(false);
      }

      // Only close color picker if click is outside AND not inside filter dropdown
      if (showColorPickerFor && !isClickInsideColorPicker && !isClickInsideFilter) {
        setShowColorPickerFor(null);
        setColorPickerPosition(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterPopup, showCalendarModal, showColorPickerFor]);

  const handleSensorChange = (channelId: string) => {
    setPendingSensors((prev) => {
      if (prev.includes(channelId)) {
        return prev.filter((id) => id !== channelId);
      } else {
        // Apply maximum 3 sensors limit only for group mode
        if (chartMode === 'group' && prev.length >= 3) {
          return prev;
        }
        return [...prev, channelId];
      }
    });
  };

  const chartDataKey = React.useMemo(() => {
    const selectedData = getSelectedSensorData(
      sensors,
      selectedSensors,
      chartDataSnapshot,
      hasValidChartData
    );
    return buildChartDataKey(
      selectedSensors,
      selectedData,
      selectedTimeRange,
      startDate,
      endDate,
      customScaling
    );
  }, [
    selectedSensors,
    sensors,
    chartDataSnapshot,
    hasValidChartData,
    selectedTimeRange,
    startDate,
    endDate,
    customScaling,
  ]);

  useAnalysisGroupChart({
    chartMode,
    selectedSensors,
    sensors,
    chartDataSnapshot,
    hasValidChartData,
    hasNoData,
    initialDataLoadComplete,
    chartDataLoading,
    customScaling,
    uomRanges,
    chartDataKey,
    chartCreated,
    lastChartDataKey,
    getDateRange,
    chartInstanceRef,
    chartCreationRef,
    chartCreationTimeoutRef,
    setChartCreated,
    setLastChartDataKey,
  });

  useEffect(() => {
  if (triggerInitialLoad) {
    // default last 1 hour logic
    applyLastOneHour();
  }
}, [triggerInitialLoad]);

  const calendarButtonLabel = formatAnalysisCalendarButtonLabel(
    selectedTimeRange,
    appliedStartDate,
    appliedEndDate,
    startDate,
    endDate
  );
  const exportEnabled = canExportAnalysisData(selectedSensors.length, isExporting);
  const showGroupChartEmptyState = shouldShowGroupChartEmptyState(
    initialDataLoadComplete,
    hasNoData,
    chartDataLoading,
    chartCreated,
    hasValidChartData,
    chartDataSnapshot.length
  );
  const scalingApplyEnabled = isCustomScalingApplyEnabled(unitScalingValues);

  return (
    <div className="analysis-tab-container">
      <div className="analysis-header">
        <div className="analysis-title-section">
          <h2>Analysis</h2>
        </div>
        <div className="analysis-controls">
          {/* Chart mode toggle buttons */}
          <div className="chart-mode-toggle">
            <button
              className={`chart-mode-btn ${chartMode === 'individual' ? 'active' : ''}`}
              onClick={() => handleChartModeChange('individual')}
            >
              Individual
            </button>
            <button
              className={`chart-mode-btn ${chartMode === 'group' ? 'active' : ''}`}
              onClick={() => handleChartModeChange('group')}
            >
              Group
            </button>
          </div>
          <div className="calendar-button-container" style={{ position: 'relative', zIndex: '7' }}>
            <div
              id='calendar-btn'
              style={{
                border: showCalendarModal
                  ? '1px solid var(--Dark-mode-400, #4A5463)'
                  : '1px solid var(--Dark-mode-400, #4A5463)',
                background: showCalendarModal ? 'var(--Dark-mode-800, #001023)' : 'inherit',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                padding: '10px 16px',
                borderRadius: '6px',
                gap: '8px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleCalendarToggle();
              }}
            >
              <img src={calendarIcon} alt='calendarIcon' width="16" height="16" />
              <span style={{ color: '#e0e0e0', fontSize: '14px' }}>
                {calendarButtonLabel}
              </span>
              <img src={downIcon} className={!showCalendarModal ? '' : 'rotate-180'} alt='downIcon' />
            </div>
            {showCalendarModal && (
              <div
                ref={calendarModalRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  zIndex: 1000,
                  background: 'transparent'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <DateRangePicker
                  sidebarOptions={sidebarOptions}
                  initialSelectedOption={getSelectedSidebarOption()}
                  onApply={({ startDate: s, endDate: e }) => {
                    setStartDate(s);
                    setEndDate(e);
                    setAppliedStartDate(s);
                    setAppliedEndDate(e);

                    const matchedTimeRange = matchPredefinedTimeRangeLabel(s, e);
                    setSelectedTimeRange(matchedTimeRange ?? 'Custom date range');

                    setShowDatePicker(false);
                    setShowCalendarModal(false);
                    handleDateRangeApply();
                  }}
                  onCancel={() => {
                    // Restore previous applied dates instead of clearing
                    setStartDate(appliedStartDate);
                    setEndDate(appliedEndDate);
                    setShowDatePicker(false);
                    setShowCalendarModal(false);
                  }}
                  initialStartDate={startDate ?? appliedStartDate ?? undefined}
                  initialEndDate={endDate ?? appliedEndDate ?? undefined}
                  showTimeInputs={true}
                />
              </div>
            )}
          </div>
          {/* Export Button */}
          <div className="export-button-container">
            <button
              className={`export-btn ${exportEnabled ? 'export-btn--enabled' : 'export-btn--disabled'}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (selectedSensors.length === 0 || isExporting) {
                  return;
                }
                handleExportData();
              }}
              disabled={selectedSensors.length === 0 || isExporting}
            >
              <img src={downloadIcon} alt='downloadIcon' width="16" height="16" />
              <span>{isExporting ? 'Downloading...' : `Download`}</span>
            </button>
          </div>
          <div className='history-container-filter'>
            <div style={{ position: 'relative', zIndex: '8' }}>
              <div
                id='filter-btn'
                style={{
                  border: showFilterPopup
                    ? '1px solid var(--Dark-mode-400, #4A5463)'
                    : '1px solid var(--Dark-mode-400, #4A5463)',
                  background: showFilterPopup ? 'var(--Dark-mode-800, #001023)' : 'inherit',
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFilterDropdownClick();
                }}
              >
                <img src={filterIcon} alt='filterIcon' />
                Sensors {`(${selectedSensors?.length})`}
                <img src={downIcon} className={!showFilterPopup ? '' : 'rotate-180'} alt='downIcon' />
              </div>

              {showFilterPopup && (
                <div className='trends-filter-modal-container' ref={filterRef}>
                  <div className='trends-filter-modal-header'>Add/Remove sensors</div>
                  <div className='trends-filter-search history-search relative'>
                    <img src={searchIcon} alt='search' className='history-search-icon' />
                    <input
                      type='text'
                      value={searchInputVal}
                      onChange={handleSearch}
                      className='history-filter-search'
                      placeholder="Search sensors..."
                    />
                  </div>
                  <div className='trends-filter-modal-body'>
                    {loading ? (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#bfc9db' }}>
                        Loading sensors...
                      </div>
                    ) : (
                      // Replace the sensor checkbox item in the filter modal (around line 700-730)
                      searchFiltered?.map((sensor) => {
                        const hasData = sensor.dataPoints && sensor.dataPoints.length > 0;
                        const isSelected = pendingSensors.includes(sensor.channelId);
                        const shouldDisable = !isSelected && chartMode === 'group' && pendingSensors.length >= 3;

                        return (
                          <div key={sensor.id} className='checkbox-main-container'>
                            <label className="sensor-checkbox-item">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleSensorChange(sensor.channelId)}
                                disabled={shouldDisable}
                              />
                              <span className="sensor-checkbox-label">
                                {/* Updated color indicator with click handler */}
                                <span
                                  className="sensor-color-indicator"
                                  style={{
                                    backgroundColor: sensor.color,
                                    cursor: 'pointer',
                                    position: 'relative'
                                  }}
                                  onClick={(e) => {
                                    e.preventDefault(); // Prevent form submission
                                    e.stopPropagation(); // Prevent checkbox change
                                    openColorPicker(sensor.id, e);
                                  }}
                                  title="Click to change color"
                                >
                                  <img
                                    src={colorPickerIcon}
                                    alt="color picker"
                                    style={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      width: '12px',
                                      height: '12px',
                                      opacity: 0.7,
                                      display: isSelected ? 'block' : 'none'
                                    }}
                                  />
                                </span>
                                <span className="sensor-info">
                                  <span className="sensor-name">{sensor.sensorName}</span>
                                  {hasData && (
                                    <span className="sensor-unit">({sensor.unit})</span>
                                  )}
                                </span>
                              </span>
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className='trends-filter-modal-footer'>
                    <button className='footer-btn' onClick={handleClearButton}>
                      Clear chart
                    </button>
                    <button className='footer-btn' onClick={handleApplyButton}>
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showColorPickerFor && colorPickerPosition && (
        <div className='color-picker-container'
          ref={colorPickerRef}

          onClick={(e) => e.stopPropagation()}
        >
          <div style={{
            marginBottom: '10px',
            color: '#fff',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            Choose color for {sensors.find(s => s.id === showColorPickerFor)?.sensorName}
          </div>

          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#8b9cb8', fontSize: '12px', marginBottom: '5px' }}>Current:</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '30px',
                  height: '30px',
                  backgroundColor: sensors.find(s => s.id === showColorPickerFor)?.color,
                  border: '2px solid #4A5463',
                  borderRadius: '4px'
                }}
              />
              <div style={{ color: '#fff' }}>
                {sensors.find(s => s.id === showColorPickerFor)?.color.toUpperCase()}
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '10px' }}>
            <div style={{ color: '#8b9cb8', fontSize: '12px', marginBottom: '5px' }}>Quick picks:</div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: '6px',
              marginBottom: '10px'
            }}>
              {colorOptions.map((color) => (
                <button
                  key={color}
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: color,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSensorColorChange(showColorPickerFor, color, true); // true = close picker
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  title={color.toUpperCase()}
                />
              ))}
            </div>
          </div>

          <div>
            <div style={{ color: '#8b9cb8', fontSize: '12px', marginBottom: '5px' }}>Custom color:</div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={sensors.find(s => s.id === showColorPickerFor)?.color || '#FFFFFF'}
                onChange={(e) => {
                  e.stopPropagation();
                  const newColor = e.target.value;
                  // Pass false to keep picker open
                  handleSensorColorChange(showColorPickerFor, newColor, false);
                }}
                style={{
                  width: '100%',
                  height: '40px',
                  border: '1px solid #4A5463',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                value={sensors.find(s => s.id === showColorPickerFor)?.color || '#FFFFFF'}
                onChange={(e) => {
                  e.stopPropagation();
                  const newColor = e.target.value;
                  // Validate hex color format
                  if (/^#[0-9A-F]{6}$/i.test(newColor)) {
                    handleSensorColorChange(showColorPickerFor, newColor);
                  }
                }}
                onBlur={(e) => {
                  const newColor = e.target.value;
                  // If not starting with #, add it
                  if (!newColor.startsWith('#') && newColor.length > 0) {
                    handleSensorColorChange(showColorPickerFor, '#' + newColor);
                  }
                }}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: '#0a1a29',
                  border: '1px solid #4A5463',
                  borderRadius: '4px',
                  color: '#fff',
                  fontSize: '12px'
                }}
                placeholder="#FFFFFF or hex code"
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              style={{
                marginTop: '12px',
                padding: '6px 12px',
                backgroundColor: 'transparent',
                border: '1px solid #4A5463',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                width: '100%',
                fontSize: '12px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setShowColorPickerFor(null);
                setColorPickerPosition(null);
              }}
            >
              Close
            </button>
            <button
              style={{
                flex: 1,
                padding: '8px 12px',
                backgroundColor: '#0078D4',
                border: '1px solid #0078D4',
                borderRadius: '4px',
                color: '#fff',
                cursor: 'pointer',
                fontSize: '12px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Apply the color (if using temporary state)
                setLastChartDataKey('');
                setShowColorPickerFor(null);
                setColorPickerPosition(null);
              }}
            >
              Apply
            </button>
          </div>
        </div>
      )}
      <div className="analysis-chart-container" style={{ width: "100%", minWidth: "800px", position: "relative" }}>
        
        {/* Custom Scaling Button - show only for group mode */}
        {chartMode === 'group' && (
          <button
            className="custom-scaling-button"
            onClick={handleCustomScalingOpen}
              style={{
                position: 'absolute',
                top: '3px',
                right: '3px',
                zIndex: showFilterPopup ? 5 : 15, 
                background: 'white',
                color: '#333333',
                border: '1px solid #cccccc',
                borderRadius: '6px',
                padding: '3px 3px',
                fontSize: '12px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: showFilterPopup ? 0.5 : 1 
              }}
              onMouseEnter={(e) => {
                if (!showFilterPopup) {
                  e.currentTarget.style.background = '#f5f5f5';
                  e.currentTarget.style.borderColor = '#999999';
                }
              }}
              onMouseLeave={(e) => {
                if (!showFilterPopup) {
                  e.currentTarget.style.background = 'white';
                  e.currentTarget.style.borderColor = '#cccccc';
                }
              }}
              title="Custom scaling"
            >
              <img src={scaleIcon} alt="Custom scaling" style={{width: '16px', height: '16px'}} />
            </button>
        )}
        
        {/* Render charts based on mode */}
        {chartMode === 'individual' ? (
          <IndividualChart
            sensors={sensors}
            selectedSensors={selectedSensors}
            selectedTimeRange={selectedTimeRange}
            customScaling={customScaling}
            uomRanges={uomRanges}
            hasNoData={hasNoData}
            initialDataLoadComplete={initialDataLoadComplete}
            chartDataLoading={chartDataLoading}
            onCustomScalingOpen={handleCustomScalingOpen}
          />
        ) : (
          // Group chart (existing functionality)
          <>
            {!sensorsInitialized ? (
              <div className="loading-message">
                <Loader />
                <p>Initializing sensors...</p>
              </div>
            ) : selectedSensors.length > 0 || chartCreated || hasValidChartData || chartDataSnapshot.length > 0 ? (
              <div style={{ position: 'relative', width: "100%" }}>
                {chartDataLoading && !chartCreated && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(10, 25, 41, 0.8)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10,
                    borderRadius: '8px',
                    gap: '12px'
                  }}>
                    <Loader />
                  </div>
                )}
                {showGroupChartEmptyState ? (
                  <div className="no-data-message">
                    <p>No data found for the selected sensors in the {selectedTimeRange.toLowerCase()} time period.</p>
                    <p style={{ fontSize: '14px', color: '#8b9cb8', marginTop: '8px' }}>
                      Try selecting a different time range or different sensors.
                    </p>
                  </div>
                ) : (
                  <div
                    id="analysis-chartdiv"
                    style={{
                      width: "100%",
                      minWidth: "800px",
                      height: "500px"
                    }}
                  />
                )}
              </div>
            ) : (
              <div className="no-data-message">
                <p>Please select at least one sensor to display the chart.</p>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Custom Scaling Modal */}
      {showCustomScalingModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              handleCustomScalingClose();
            }
          }}
        >
          <div 
            className="custom-scaling-modal"
            style={{
              background: '#0a1929',
              border: '1px solid #4a5463',
              borderRadius: '8px',
              width: '270px',
              color: '#e0e0e0',
              marginTop: '50px',
              paddingLeft: '8px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: '8px',
              paddingBottom: '10px',
            }}>
              <h3 style={{
                color: '#fff',
                fontSize: '18px',
                fontWeight: '600',
                margin: '0',
                flexGrow: 1,
                textAlign: 'center'
              }}>
                Custom Scaling
              </h3>
              <button
                onClick={handleCustomScalingClose}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#e0e0e0',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  width: '28px',
                  height: '28px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#22314a';
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#e0e0e0';
                }}
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div 
              style={{ 
                marginBottom: '24px', 
                maxHeight: '400px', 
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#4a5463 #162032'
              }}
              ref={(el) => {
                if (el) {
                  const style = document.createElement('style');
                  style.textContent = `
                    .custom-scaling-scroll::-webkit-scrollbar {
                      width: 6px;
                    }
                    .custom-scaling-scroll::-webkit-scrollbar-track {
                      background: #162032;
                      border-radius: 3px;
                    }
                    .custom-scaling-scroll::-webkit-scrollbar-thumb {
                      background: #4a5463;
                      border-radius: 3px;
                    }
                    .custom-scaling-scroll::-webkit-scrollbar-thumb:hover {
                      background: #5a6573;
                    }
                  `;
                  if (!document.head.querySelector('.custom-scaling-scrollbar-style')) {
                    style.className = 'custom-scaling-scrollbar-style';
                    document.head.appendChild(style);
                  }
                  el.className = 'custom-scaling-scroll';
                }
              }}
            >
              {Object.entries(unitScalingValues).map(([unit, values], index, array) => (
                <div key={unit}>
                  {/* Axis Field */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{
                      color: '#bfc9db',
                      fontSize: '14px'
                    }}>
                      Axis : {values.displayName || unit}
                    </div>
                  </div>
                  
                  {/* Min and Max Fields in a row */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0px',
                    marginBottom: '16px'
                  }}>
                    <div>
                      <label style={{
                        display: 'block',
                        color: '#bfc9db',
                        fontSize: '14px',
                        marginBottom: '8px'
                      }}>
                        Min
                      </label>
                      <input
                        type="number"
                        value={values.min}
                        onChange={(e) => handleUnitValueChange(unit, 'min', e.target.value)}
                        placeholder="Min value"
                        style={{
                          width: '70%',
                          background: '#22314a',
                          color: '#e0e0e0',
                          border: '1px solid #4a5463',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{
                        display: 'block',
                        color: '#bfc9db',
                        fontSize: '14px',
                        marginBottom: '8px'
                      }}>
                        Max
                      </label>
                      <input
                        type="number"
                        value={values.max}
                        onChange={(e) => handleUnitValueChange(unit, 'max', e.target.value)}
                        placeholder="Max value"
                        style={{
                          width: '70%',
                          background: '#22314a',
                          color: '#e0e0e0',
                          border: '1px solid #4a5463',
                          borderRadius: '4px',
                          padding: '8px 12px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Horizontal separator line - only show if not the last item */}
                  {index < array.length - 1 && (
                    <div style={{
                      width: '100%',
                      height: '1px',
                      background: '#4a5463',
                      margin: '16px 0'
                    }} />
                  )}
                </div>
              ))}
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '-20px',
              paddingBottom: '10px'
            }}>
                <button
                  onClick={handleCustomScalingClose}
                  style={{
                    background: 'transparent',
                    color: '#e0e0e0',
                    border: '1px solid #4a5463',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#22314a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCustomScalingApply}
                  disabled={!scalingApplyEnabled}
                  style={{
                    background: scalingApplyEnabled ? '#1ec9a7' : '#4a5463',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    cursor: scalingApplyEnabled ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    marginRight: '14px'
                  }}
                  onMouseEnter={(e) => {
                    if (scalingApplyEnabled) {
                      e.currentTarget.style.background = '#17b496';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (scalingApplyEnabled) {
                      e.currentTarget.style.background = '#1ec9a7';
                    }
                  }}
                >
                  Apply
                </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AnalysisTab: React.FC<AnalysisTabProps> = (props) => <AnalysisTabContent {...props} />;

export default AnalysisTab;